export const ARTICLES = [
  {
    slug: "year-end-close-checklist-small-business",
    title: "Your year-end close checklist: what to hand your CPA before December 31",
    excerpt:
      "A calm December beats a chaotic March. The short list that gets your books hand-off ready — and usually lowers your CPA bill.",
    date: "August 2026",
    readTime: "4 min read",
    category: "Small business",
    body: [
      {
        t: "p",
        text: "Every accountant knows the two seasons of a small business: the calm fall, when there is still time, and the frantic spring, when there isn't. The difference between them is usually a single focused afternoon in November or December spent getting your books hand-off ready. Here is the exact list we run with our own clients.",
      },
      { t: "h2", text: "The year-end checklist" },
      {
        t: "ul",
        items: [
          "Reconcile every bank and credit card account through December 31",
          "Clear the \"uncategorized\" bucket down to zero",
          "Review accounts receivable — collect what is collectible, write off what isn't, and document why",
          "Gather receipts and invoices for major purchases and equipment",
          "Confirm payroll records are clean, and collect a W-9 from every contractor you paid $600 or more",
          "Record owner draws, distributions, and any loans properly",
          "Write down the year's big changes — new vehicles, locations, staff, or loans — so your CPA hears them from you, not from a surprise line item",
        ],
      },
      { t: "h2", text: "Why October beats March" },
      {
        t: "p",
        text: "In March, your CPA is triaging. In October, they can actually think. Handing over clean books in the fall turns tax preparation into tax planning — real conversations about retirement contributions, equipment timing, and how income lands — instead of a compliance scramble. It also tends to lower the bill, because tidy books take fewer billable hours.",
      },
      {
        t: "p",
        text: "If that list looks longer than the evenings you have left, that is the signal. CalCor Solutions runs year-end closes for small businesses across Seattle and remotely anywhere — your CPA receives a package so tidy they may actually thank you. The first conversation is free.",
      },
    ],
  },
  {
    slug: "tax-season-without-the-scramble",
    title: "Tax season without the scramble: a fall game plan for small businesses",
    excerpt:
      "Extension deadlines hit in September and October, and the best planning window closes in December. Fall is when tax season is actually won.",
    date: "August 2026",
    readTime: "4 min read",
    category: "Clarity",
    body: [
      {
        t: "p",
        text: "Most businesses treat tax season as a spring event. It isn't. Tax season is won or lost in the fall — when extension deadlines land and the year's last planning moves are still on the table. A little structure now replaces a lot of stress later.",
      },
      { t: "h2", text: "If you filed an extension" },
      {
        t: "p",
        text: "Extension deadlines for business returns arrive in mid-September and mid-October, depending on your entity type. Check which one is yours today — not the week before. Then three quick moves:",
      },
      {
        t: "ul",
        items: [
          "Confirm your exact extension deadline and put it on the calendar with a two-week buffer",
          "Get every account reconciled through the most recent month, so the numbers your return is built on are real",
          "Book the handoff call with your CPA now, while they still have room in their calendar",
        ],
      },
      { t: "h2", text: "If you're planning for next spring" },
      {
        t: "ul",
        items: [
          "Commit to books that are fully current by mid-January",
          "Review your Q4 estimated tax payment against how the year actually went",
          "Ask your CPA about retirement contributions and the QBI deduction before December 31 — after that, most levers are locked",
          "Collect contractor W-9s now, not during January's 1099 rush",
        ],
      },
      {
        t: "p",
        text: "The common thread: clean, current books are the raw material of a calm tax season. If keeping them current is the piece that keeps slipping, that is exactly what we do all year at CalCor Solutions — and the first conversation costs nothing but thirty minutes.",
      },
    ],
  },
  {
    slug: "single-audit-preparation-tribal-governments",
    title: "A plain-language guide to single audit preparation for tribal governments",
    excerpt:
      "What a single audit actually involves, which documents make or break fieldwork, and when to start preparing — without the jargon.",
    date: "July 2026",
    readTime: "6 min read",
    category: "Tribal governments",
    body: [
      {
        t: "p",
        text: "If your tribe spends more than $1,000,000 in federal awards in a fiscal year (for fiscal years beginning on or after October 1, 2024 — the threshold was $750,000 before that), you are required to have an annual single audit under the Uniform Guidance. For many finance teams, those words arrive with a quiet sense of dread. They shouldn't. A single audit is simply a structured review — and like most structured reviews, it rewards preparation far more than it punishes mistakes.",
      },
      { t: "h2", text: "What the auditors actually test" },
      {
        t: "p",
        text: "A single audit has two halves. The first examines your financial statements, much like any audit. The second tests compliance: for each major federal program, auditors check whether spending followed the program's rules — allowable costs, eligibility, reporting, procurement, and cash management. They also test the internal controls you have over those areas, which is why written policies matter as much as clean numbers.",
      },
      { t: "h2", text: "The documents that make or break fieldwork" },
      {
        t: "ul",
        items: [
          "A current, reconciled trial balance tied to your general ledger",
          "The Schedule of Expenditures of Federal Awards (SEFA) — started early, not the week before",
          "Grant agreements, award letters, and any amendments",
          "Bank and account reconciliations with clear supporting schedules",
          "Payroll records showing how staff time is allocated to programs",
          "Written procurement and cash management policies",
          "Your indirect cost rate agreement or de minimis election documentation",
        ],
      },
      { t: "h2", text: "When to start" },
      {
        t: "p",
        text: "The honest answer: ninety days before the auditors arrive, at minimum. The SEFA alone benefits from being built as the year unfolds rather than reconstructed from memory. When the auditor's PBC (provided-by-client) list lands, every item should already have a home in your files.",
      },
      { t: "h2", text: "How we help" },
      {
        t: "p",
        text: "CalCor Solutions prepares tribal governments for exactly this moment: audit-ready workpapers, SEFA support, reconciliations that tie out, and a calm liaison with your auditors so fieldwork feels routine. We work around council calendars and handle every document with the discretion these engagements deserve.",
      },
      {
        t: "p",
        text: "If a single audit is on your horizon this year, the kindest thing you can do for your future self is start the conversation early. The first call is free, thirty minutes, and refreshingly plain-English.",
      },
    ],
  },
  {
    slug: "bookkeeping-accounting-audit-prep-difference",
    title: "Bookkeeping, accounting, or audit preparation: which does your organization actually need?",
    excerpt:
      "The three words get used interchangeably, but they do different jobs. Here's how to tell which one your situation calls for.",
    date: "August 2026",
    readTime: "4 min read",
    category: "Clarity",
    body: [
      {
        t: "p",
        text: "Bookkeeping, accounting, and audit preparation get lumped together so often that many owners and directors end up buying the wrong one — or paying for all three when they need one. Each does a distinct job, and knowing the difference saves real money.",
      },
      { t: "h2", text: "Bookkeeping: the daily record" },
      {
        t: "p",
        text: "Bookkeeping is the disciplined recording of what actually happened — every transaction categorized, every account reconciled, payables and receivables tracked. Done well, it is quiet and invisible. Done poorly (or not at all), everything built on top of it wobbles.",
      },
      { t: "h2", text: "Accounting: the bigger picture" },
      {
        t: "p",
        text: "Accounting turns those records into meaning: monthly closes, financial statements your board or lender can rely on, budgets, grant and fund tracking, and answers to questions like \"can we afford this hire?\" If bookkeeping is the raw footage, accounting is the edited film.",
      },
      { t: "h2", text: "Audit preparation: the rehearsal" },
      {
        t: "p",
        text: "Audit preparation is the work of being ready before the auditors arrive — workpapers, lead schedules, reconciliations, and responses to the PBC list, so fieldwork confirms your discipline instead of exposing your stress. It matters most when an audit is scheduled or a grant requires one.",
      },
      { t: "h2", text: "So which one do you need?" },
      {
        t: "ul",
        items: [
          "Weeks behind on categorizing transactions? Start with bookkeeping.",
          "Books are current, but reports confuse your board or council? You need accounting support.",
          "An audit is scheduled — or federal awards require one? You need audit preparation.",
          "Not sure? That is completely normal, and it is exactly what a first consultation is for.",
        ],
      },
      {
        t: "p",
        text: "Most organizations need a blend, and the right mix changes as you grow. At CalCor Solutions we scope that blend together in one plain-language conversation — no jargon, no pressure, and no fee for the first call.",
      },
    ],
  },
  {
    slug: "signs-outgrown-diy-bookkeeping-seattle",
    title: "Five signs your Seattle small business has outgrown DIY bookkeeping",
    excerpt:
      "Doing your own books works — until it quietly doesn't. Five honest signals it's time to hand the ledger to a professional.",
    date: "July 2026",
    readTime: "4 min read",
    category: "Small business",
    body: [
      {
        t: "p",
        text: "Nearly every Seattle small business starts the same way: the owner, a spreadsheet or a QuickBooks file, and a Sunday evening. That works — genuinely — until the business grows past the point where evenings can keep up. Here are the five signals we see most often when it is time to hand the books to a professional.",
      },
      {
        t: "ul",
        items: [
          "Your books are always a few weeks (or months) behind, so every decision uses stale numbers.",
          "Tax season means a scramble — and a nervous call to your CPA in March.",
          "You produce reports you don't fully trust, so you quietly ignore them.",
          "Washington's B&O tax and Seattle's local filings keep surprising you.",
          "You're making growth decisions — hiring, pricing, a second location — on gut feel rather than current numbers.",
        ],
      },
      {
        t: "p",
        text: "If two or more of those sound familiar, the issue is not discipline — it is capacity. Your business simply outgrew the hours you can give it, which is a good problem to have.",
      },
      { t: "h2", text: "What handing it over actually looks like" },
      {
        t: "p",
        text: "Simpler than most owners expect. Transactions flow in automatically, we reconcile and close each month, and you receive a short reporting package in plain English — what happened, what changed, what to watch. One fixed monthly fee, one recurring check-in call, and your Sunday evenings back.",
      },
      {
        t: "p",
        text: "CalCor Solutions works with small businesses across Seattle and remotely anywhere in Washington and beyond. If this article felt uncomfortably familiar, that first conversation is free — thirty minutes, no pressure, and you'll leave knowing exactly what shape your books are in.",
      },
    ],
  },
];
